/**
 * SPDX-License-Identifier: MPL-2.0
 * Copyright 2022 (c) Karsten Röthig, FVA GmbH (for umati and VDW e.V.)
 * Copyright 2024 (c) Daniel Littfinski, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2026 (c) Nico Brandt, ISW University of Stuttgart (for umati and VDW e.V.)
 */
import React, { useState, useEffect } from 'react';
import { Collapse, Form, FormGroup, Row, Col, Button, Card, CardBody } from 'react-bootstrap';
import { UserActions } from './UserActions';
import { connect, useDispatch } from 'react-redux';
import './UserList.scss';
import { UserClient } from 'Api/generated';
import Select from 'react-select';
import { ICompaniesState } from 'CompanyData/CompanyReducer';
import { RootState } from 'reducer';
import { AllowedRoles } from 'Contexts/Auth';

interface UserProps {
  user: UserClient;
  isAdd: boolean;
  companies: ICompaniesState;
}

const User = ({ user, isAdd, companies }: UserProps) => {
  const [state, setState] = useState({ ...user });
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    setState(user);
    if (isAdd && companies.Companies.length === 1) {
      setState({ ...user, CompanyIds: [companies.Companies[0].Id || ''] });
    }
  }, [user]);

  const getKeys = Object.keys as <T extends object>(obj: T) => Array<keyof T>;

  function onInput<K extends keyof UserClient>(key: K, value: UserClient[K]) {
    const s = { ...state, [key]: value };
    setState(s);
  }

  const deleteUser = () => {
    UserActions.DeleteUser(state)(dispatch);
  };

  const saveUser = () => {
    UserActions.UpdateUser(state)(dispatch);
  };

  const addUser = () => {
    UserActions.CreateUser(state)(dispatch);
  };

  const setAdminRole = () => {
    setState({ ...state, Roles: ['Admin'] });
    UserActions.SetAdminRole(state)(dispatch);
  };

  const setMemberRole = () => {
    setState({ ...state, Roles: ['CompanyMember'] });
    UserActions.SetMemberRole(state)(dispatch);
  };

  const setSuperAdminRole = () => {
    setState({ ...state, Roles: ['SuperAdmin'] });
    UserActions.SetSuperAdminRole(state)(dispatch);
  };

  const isReadOnly = (field: string) => {
    const readOnlyFields = ['Id'];
    return readOnlyFields.includes(field);
  };

  const allpossibleOptions = companies.Companies.map((comps) => ({
    value: comps.Id,
    label: comps.Name,
  }));

  const selectedValues = allpossibleOptions.filter((opt) => state.CompanyIds?.includes(opt.value || ''));
  //const slecteableOptions = allpossibleOptions.filter(opt => !state.CompanyIds?.includes(opt.value || ''));

  const handleChangeSelectedComps = (e: any) => {
    const ids = e.map((e: any) => e.value);
    console.log('Data On Change: ', ids);
    setState({ ...state, CompanyIds: [...ids] });
    console.log('State: ', state);
  };

  return (
    <li className="list-group-item" id={state.Username || undefined}>
      <Card>
        <CardBody>
          <b>
            {`${state.Username}|${state.Id}`}
            <Button
              onClick={() => setOpen(!open)}
              aria-controls="example-collapse-text"
              variant="Light"
              aria-expanded={open}
            >
              Expand
            </Button>
          </b>
          {isAdd === false ? (
            <>
              <Button className="btn-inline right" onClick={() => saveUser()}>
                Save
              </Button>
              <Button className="btn-inline right" onClick={() => deleteUser()} variant="danger">
                Delete
              </Button>
            </>
          ) : (
            <Button
              className="btn-inline right"
              disabled={!state.CompanyIds || state.CompanyIds.length === 0}
              onClick={() => addUser()}
            >
              Add
            </Button>
          )}
          <Collapse in={open}>
            <div>
              <Form>
                {getKeys(state).map((key: keyof UserClient) => {
                  const val = state[key];
                  return (
                    (typeof val === 'string' || typeof val === 'number' || typeof val === 'undefined') &&
                    key !== 'Password' && (
                      <FormGroup as={Row} key={key}>
                        <Form.Label column sm="2">
                          {key}
                        </Form.Label>
                        <Col sm="10">
                          <Form.Control
                            as="input"
                            value={val}
                            id={key}
                            onChange={(e) => onInput(key, e.target.value)}
                            readOnly={isReadOnly(key)}
                          />
                        </Col>
                      </FormGroup>
                    )
                  );
                })}
                <Form.Group as={Row}>
                  <Form.Label column sm="2">
                    Password
                  </Form.Label>
                  <Col sm="10">
                    <Form.Control
                      type="password"
                      value={state.Password || undefined}
                      id="Password"
                      onChange={(e) => onInput('Password', e.target.value)}
                      placeholder="Set password (leave blank to keep old password)"
                    />
                  </Col>
                </Form.Group>
              </Form>
              {isAdd === false ? (
                <Form.Group as={Row}>
                  <Form.Label column sm="2">
                    Select Role
                  </Form.Label>
                  <Col sm="10">
                    <AllowedRoles roles={['SuperAdmin']}>
                      {state.Roles?.includes('SuperAdmin') ? (
                        <Button className="btn-inline left" onClick={() => setSuperAdminRole()} variant="info">
                          SuperAdmin
                        </Button>
                      ) : (
                        <Button className="btn-inline left" onClick={() => setSuperAdminRole()} variant="secondary">
                          SuperAdmin
                        </Button>
                      )}
                    </AllowedRoles>
                    {state.Roles?.includes('Admin') ? (
                      <Button className="btn-inline left" onClick={() => setAdminRole()} variant="info">
                        AdminRole
                      </Button>
                    ) : (
                      <Button className="btn-inline left" onClick={() => setAdminRole()} variant="secondary">
                        AdminRole
                      </Button>
                    )}{' '}
                    {state.Roles?.includes('CompanyMember') ? (
                      <Button className="btn-inline left" onClick={() => setMemberRole()} variant="info">
                        CompanyMemberRole
                      </Button>
                    ) : (
                      <Button className="btn-inline left" onClick={() => setMemberRole()} variant="secondary">
                        CompanyMemberRole
                      </Button>
                    )}
                  </Col>
                  <Form.Label column sm="2">
                    Client Company Access{' '}
                  </Form.Label>
                  <Col sm="10">
                    <Select
                      closeMenuOnSelect={false}
                      value={selectedValues}
                      isMulti
                      onChange={handleChangeSelectedComps}
                      options={allpossibleOptions}
                    />
                  </Col>
                </Form.Group>
              ) : (
                // Beim Anlegen eines neuen Users: Single-Select für eine Company
                <Form.Group as={Row}>
                  <Form.Label column sm="2">
                    Client Company Access
                  </Form.Label>
                  <Col sm="10">
                    <Select
                      value={allpossibleOptions.find((opt) => opt.value === state.CompanyIds?.[0]) || null}
                      onChange={(e) => {
                        if (e && e.value) setState({ ...state, CompanyIds: [e.value as string] });
                      }}
                      options={allpossibleOptions}
                    />
                  </Col>
                </Form.Group>
              )}
              <br></br>
            </div>
          </Collapse>
        </CardBody>
      </Card>
    </li>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    users: state.users,
    companiesOfUser: state.companies.Companies, //getFilteredCompanies(state, user),
  };
};

export default connect(mapStateToProps)(User);
